import { useEffect, useMemo, useRef, useState, type CSSProperties, type FocusEvent } from 'react';
import {
  Checklist, DateField, Sheet, SheetField, SheetSection, UserPicker
} from '../primitives';
import { WysiwygEditor, type WysiwygSaveState } from '../primitives/WysiwygEditor';
import { parseDod, statusToTone } from './helpers';
import {
  addCriterion, newCriterion, normalize, removeCriterion, reorderCriteria, setText, toggleMet
} from './dod';
import { useActiveOrg } from '../../lib/activeOrg';
import { useProjectSheetData } from '../../lib/projects/useProjectSheetData';
import type { ProjectPatch } from '../../lib/projects/useSaveProject';
import type { DodCriterion, ProjectRow } from './types';

export interface ProjectSettingsSheetProps {
  open: boolean;
  project: ProjectRow;
  onClose: () => void;
  /** Persists a partial patch (one field per change) and reloads the canvas. */
  onPatch: (patch: Partial<ProjectPatch>) => Promise<boolean> | void;
}

/** Drop blank criteria + re-pack positions for the stored value. */
function serializeDod(items: ReadonlyArray<DodCriterion>): DodCriterion[] {
  return normalize(items).filter((c) => c.text.trim().length > 0);
}

/**
 * Project "Details" inspector — mirrors the Documents/Work-Items inspector:
 * every field autosaves on change (no Save/Cancel), and the panel dismisses
 * via Esc / outside-click / the × button. Title + description live on the
 * page header, so this panel holds status / lead / target / Definition of Done.
 */
export function ProjectSettingsSheet({ open, project, onClose, onPatch }: ProjectSettingsSheetProps) {
  const { activeOrgId } = useActiveOrg();
  const { members, stages } = useProjectSheetData(activeOrgId, open);

  const initialDod = useMemo(() => normalize(parseDod(project.definition_of_done)), [project.definition_of_done]);

  const [status, setStatus] = useState(project.status);
  const [leadId, setLeadId] = useState<string | null>(project.lead_id);
  const [targetDate, setTargetDate] = useState<string | null>(project.target_date);
  const [dod, setDod] = useState<DodCriterion[]>(initialDod);
  const [overviewDraft, setOverviewDraft] = useState(project.overview ?? '');
  const [overviewSaveState, setOverviewSaveState] = useState<WysiwygSaveState>('idle');
  const overviewDraftRef = useRef(overviewDraft);
  overviewDraftRef.current = overviewDraft;

  // The sheet stays mounted (so the open/close slide animates) — re-seed the
  // fields from the row each time it opens, in case the project changed while
  // closed. (Replaces the previous key-remount, which killed the animation.)
  useEffect(() => {
    if (!open) return;
    setStatus(project.status);
    setLeadId(project.lead_id);
    setTargetDate(project.target_date);
    setDod(normalize(parseDod(project.definition_of_done)));
    setOverviewDraft(project.overview ?? '');
    setOverviewSaveState('idle');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const pickStatus = (s: string) => { setStatus(s); void onPatch({ status: s }); };
  const pickLead = (id: string | null) => { setLeadId(id); void onPatch({ lead_id: id }); };
  const pickTarget = (d: string | null) => { setTargetDate(d); void onPatch({ target_date: d }); };

  const commitOverview = () => {
    const draft = overviewDraftRef.current;
    if (draft === (project.overview ?? '')) return;
    setOverviewSaveState('saving');
    void Promise.resolve(onPatch({ overview: draft })).then((ok) => {
      setOverviewSaveState(ok === false ? 'failed' : 'saved');
    });
  };
  const onOverviewAreaBlur = (e: FocusEvent<HTMLDivElement>) => {
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    commitOverview();
  };

  // Structural DoD edits persist immediately; criterion text persists on blur
  // (so we don't write once per keystroke).
  const persistDod = (next: DodCriterion[]) => { setDod(next); void onPatch({ definition_of_done: serializeDod(next) }); };
  const commitDodText = () => { void onPatch({ definition_of_done: serializeDod(dod) }); };

  const stageList = stages.length > 0 ? stages : [project.status];
  const leadValue = leadId ? { user_id: leadId } : null;

  return (
    <Sheet
      open={open}
      onClose={onClose}
      kicker="Project"
      title="Project settings"
      subtitle={project.name}
      ariaLabel="Project settings"
      dataTestId="project-settings"
      wide
    >
      <div data-testid="project-settings-sheet">
        <SheetSection label="Status">
          <div className="pn-segmented" role="radiogroup" aria-label="Project status">
            {stageList.map((s) => (
              <button
                key={s}
                type="button"
                role="radio"
                aria-checked={s === status}
                className="pn-segmented-item"
                style={{ '--tone': statusToTone(s) } as CSSProperties}
                onClick={() => pickStatus(s)}
                data-testid={`project-sheet-status-${s}`}
              >
                {s.replace(/_/g, ' ')}
              </button>
            ))}
          </div>
        </SheetSection>

        <SheetSection label="Details">
          <div className="pn-sheet-grid2">
            <SheetField label="Project lead" htmlFor="project-sheet-lead">
              <UserPicker
                id="project-sheet-lead"
                label="Project lead"
                options={members.map((m) => ({ user_id: m.user_id, display_name: m.display_name, username: m.username ?? null }))}
                value={leadValue}
                onPick={(opt) => pickLead(opt?.user_id ?? null)}
                placeholder="Unassigned"
                data-testid="project-sheet-lead"
              />
            </SheetField>
            <SheetField label="Target completion" htmlFor="project-sheet-target">
              <DateField
                id="project-sheet-target"
                label="Target completion"
                hideLabel
                value={targetDate}
                onChange={pickTarget}
                testId="project-sheet-target"
              />
            </SheetField>
          </div>
        </SheetSection>

        <SheetSection label="Overview">
          <div onBlur={onOverviewAreaBlur}>
            <WysiwygEditor
              value={overviewDraft}
              onChange={setOverviewDraft}
              onSave={commitOverview}
              saveState={overviewSaveState}
              showFormattingToolbar
              hideInlineSave
              flush
              placeholder="Write the project overview — what it is and what the plan is…"
              dataTestId="project-overview"
            />
          </div>
        </SheetSection>

        <SheetSection label="Definition of Done">
          <Checklist
            items={normalize(dod)}
            onToggle={(id) => persistDod(toggleMet(dod, id))}
            onChangeText={(id, text) => setDod(setText(dod, id, text))}
            onCommitText={commitDodText}
            onRemove={(id) => persistDod(removeCriterion(dod, id))}
            onReorder={(orderedIds) => persistDod(reorderCriteria(dod, orderedIds))}
            onAdd={() => setDod(addCriterion(dod, newCriterion(project.id)))}
            rowTestId={(id) => `project-sheet-dod-row-${id}`}
            addTestId="project-sheet-dod-add"
          />
        </SheetSection>
      </div>
    </Sheet>
  );
}
